import Logging from "logging";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import React, { ErrorInfo } from "react";
import { Navigate } from "react-router-dom";
import NativeBridge from "services/native-bridge/native-bridge";
import { getParameterByName } from "utils";
import {
  getPaxSessionRelativeUrl,
  getSessionRelativeUrl,
} from "./shared/hooks/navigation/useFlowRedirect";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  caughtError?: Error;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, caughtError: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // TODO Alex take from params, not search query
    const paxSessionId = getParameterByName("paxSessionId");

    Logging.log("error-boundary", paxSessionId || "missing", error.message);

    NativeBridge.shared.bridgeError(error.name || "error", error.message);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.caughtError instanceof ItemLevelNavigationError) {
        // incorrect item level navigation
        // - save from falling to "not-found" by redirecting to checklist
        const caughtError = this.state.caughtError;

        if (caughtError.paxSessionId) {
          return (
            <Navigate
              to={`${getPaxSessionRelativeUrl(
                caughtError.sessionId,
                caughtError.paxSessionId
              )}/checklist`}
            />
          );
        }

        // navigate to session root (start)
        return <Navigate to={getSessionRelativeUrl(caughtError.sessionId)} />;
      }

      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
